import React from "react";
import { connect } from "react-redux";

import { PanelContext } from "core/decorators/Panel";
import initialState from "core/reducers/initialState";
import DictionaryUtil from "core/util/dictionary/DictionaryUtil";

/**
 * utility component for resolving panel label values for use in third-party components.
 * originally created for use with react-big-calendar, but probably useful elsewhere as well (needed to pass a map of strings for message overrides).
 *
 * use like:
 *
 * <PickLabels keys={["view", "buy", "whatever"]}>
 *   {(messages) => <BigCalendar messages={messages} />}
 * </PickLabels>
 *
 * @author chris
 */

interface IPickLabelsOwnProps<T extends { [key: string]: string; }> {
	children: (messages: T) => JSX.Element; // CaaF with resolved messages
	labels: T;
	instances?: string[]; // panel instance and panel id will be included automatically if within panel context
}

interface IPickLabelsStateProps<T> {
	messages: T;
}

type ResultProps<T extends { [key: string]: string; }> = IPickLabelsStateProps<T> & IPickLabelsOwnProps<T>;

function mapStateToProps<T extends { [key: string]: string; }>(state: typeof initialState = initialState, ownProps: IPickLabelsOwnProps<T>): IPickLabelsStateProps<T> {
	// get TokenSession language
	const language: string = state.user.language;

	return {
		messages: DictionaryUtil.pickValues(state.dictionary, ownProps.labels, language, ownProps.instances) as T
	};
}

function ValueResolver<T extends { [key: string]: string}>(props: ResultProps<T>) {
	return props.children(props.messages);
}

const ConnectedValueResolver = connect(mapStateToProps)(ValueResolver);

export default function<T extends { [key: string]: string}>({instances, ...restProps}: IPickLabelsOwnProps<T>) {
	return (
		<PanelContext.Consumer>
			{(ctx) => <ConnectedValueResolver {...restProps} instances={ctx ? [...instances || [], ctx.instance, ctx.panelId] : instances} />}
		</PanelContext.Consumer>
	);
}



// WEBPACK FOOTER //
// ./src/core/components/label/PickLabels.tsx